import { formatDistanceStrict } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import isDate from 'lodash/isDate';

import { locales } from './relativeTimeFormattersLocales';

export interface DurationProps {
  date1: Date;
  date2: Date;
}

export interface RelativeTimeFormatters {
  formatRelativeTime: (date: Date) => string;
  formatDuration: (dates: DurationProps) => string;
}

export const isDurationProps = (value: unknown): value is DurationProps =>
  !!value &&
  (value as DurationProps).date1 !== undefined &&
  isDate((value as DurationProps).date1) &&
  (value as DurationProps).date2 !== undefined &&
  isDate((value as DurationProps).date2);

const relativeTimeFormatOptions: Intl.RelativeTimeFormatOptions = { numeric: 'always' };

const DIVISIONS: { amount: number; name: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, name: 'seconds' },
  { amount: 60, name: 'minutes' },
  { amount: 24, name: 'hours' },
  { amount: 7, name: 'days' },
  { amount: 4.34524, name: 'weeks' },
  { amount: 12, name: 'months' },
];

const formatRelativeTime = (date: Date, formatter: Intl.RelativeTimeFormat) => {
  // Duration in seconds
  let duration = (date.valueOf() - new Date(Date.now()).valueOf()) / 1000;
  // Iterate over each division to calculate the largest unit that is smaller than
  // the provided time, then round to the nearest integer. Example 3601 seconds => 60.02 minutes => 1.0003 hours => 1 hour.
  for (let i = 0; i < DIVISIONS.length; i += 1) {
    const division = DIVISIONS[i];
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.name);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), 'years');
};

const formatDuration = (dates: DurationProps, locale: string): string => {
  const localeObject = locales.get(locale) || enUS;

  return formatDistanceStrict(dates.date1, dates.date2, {
    locale: localeObject,
  });
};

const getRelativeTimeFormatters = (lng: string) => ({
  formatRelativeTime: (date: Date) =>
    formatRelativeTime(date, new Intl.RelativeTimeFormat(lng, relativeTimeFormatOptions)),
  formatDuration: (dates: DurationProps) => formatDuration(dates, lng),
});

export default getRelativeTimeFormatters;
